import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Info, Loader2, Save, Send, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/PhoneInput';
import { apiFetch, uploadBrandLogo } from '@/lib/api';
import { useT } from '@/lib/i18n';

interface Brand {
  about: string;
  display_name: string;
  logo_url: string;
  website: string;
  email: string;
  phone: string;
  address: string;
  description: string;
}

const EMPTY: Brand = { about: '', display_name: '', logo_url: '', website: '', email: '', phone: '', address: '', description: '' };

const fieldCls =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors ' +
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:border-ring';

/**
 * Identidade dos NÚMEROS (kit de marca + "Sobre") de um PROJETO específico.
 * `canApply` habilita o envio do "Sobre" aos números (só faz sentido no projeto ativo).
 */
export function BrandIdentityCard({ projectId, canApply = false }: { projectId: string; canApply?: boolean }) {
  const t = useT();
  const [brand, setBrand] = useState<Brand>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch('GET', `/projects/${projectId}/brand`).then((r) => {
      if (r.ok && r.data && typeof r.data === 'object') setBrand({ ...EMPTY, ...(r.data as Partial<Brand>) });
      else setBrand(EMPTY);
      setLoading(false);
    });
  }, [projectId]);

  const set = (k: keyof Brand, v: string) => setBrand((b) => ({ ...b, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiFetch('PUT', `/projects/${projectId}/brand`, brand);
      if (r.ok) toast.success(t('brand.saved'));
      else toast.error(t('brand.saveFailed'));
    } finally { setSaving(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const r = await apiFetch('POST', '/brand/apply');
      const d = r.data as { applied?: number; total?: number; code?: string } | null;
      if (r.ok) toast.success(t('brand.applied', { n: d?.applied ?? 0, total: d?.total ?? 0 }));
      else if (d?.code === 'about_required') toast.error(t('brand.aboutRequired'));
      else toast.error(t('brand.applyFailed'));
    } finally { setApplying(false); }
  };

  const onLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadBrandLogo(file);
      set('logo_url', url);
      toast.success(t('brand.logoUploaded'));
    } catch (err) {
      toast.error((err as Error).message === 'uploads_disabled' ? t('brand.uploadsDisabled') : t('brand.logoFailed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-5 p-5">
        <div>
          <h2 className="text-base font-semibold">{t('brand.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('brand.subtitle')}</p>
        </div>

        {/* Aviso: foto e nome do PERFIL são responsabilidade do cliente no celular. */}
        <div className="flex gap-3 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <Info className="mt-0.5 size-4 shrink-0" />
          <p>{t('brand.deviceNotice')}</p>
        </div>

        {/* "Sobre" — o único campo aplicável ao perfil do WhatsApp. */}
        <div className="flex flex-col gap-1.5">
          <Label>{t('brand.about')}</Label>
          <textarea
            className={fieldCls + ' h-20 py-2'}
            maxLength={139}
            placeholder={t('brand.aboutPlaceholder')}
            value={brand.about}
            onChange={(e) => set('about', e.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t('brand.aboutHint')} · {brand.about.length}/139</p>
        </div>

        {/* Kit de marca (referência / cartões de contato). */}
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('brand.kitTitle')}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* logo */}
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label>{t('brand.logo')}</Label>
              <div className="flex items-center gap-3">
                <div className="flex size-14 items-center justify-center overflow-hidden rounded-lg border bg-muted/40">
                  {brand.logo_url
                    ? <img src={brand.logo_url} alt="logo" className="size-full object-cover" />
                    : <ImageIcon className="size-5 text-muted-foreground" />}
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogo} />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                  {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />} {t('brand.logoUpload')}
                </Button>
              </div>
            </div>
            <Field label={t('brand.displayName')} value={brand.display_name} onChange={(v) => set('display_name', v)} placeholder="bZapper" />
            <Field label={t('brand.website')} value={brand.website} onChange={(v) => set('website', v)} placeholder="https://" />
            <Field label={t('brand.email')} value={brand.email} onChange={(v) => set('email', v)} placeholder="contato@empresa.com" />
            <div className="flex flex-col gap-1.5">
              <Label>{t('brand.phone')}</Label>
              <PhoneInput value={brand.phone} onChange={(v) => set('phone', v)} />
            </div>
            <div className="sm:col-span-2"><Field label={t('brand.address')} value={brand.address} onChange={(v) => set('address', v)} /></div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label>{t('brand.description')}</Label>
              <textarea className={fieldCls + ' h-16 py-2'} value={brand.description} onChange={(e) => set('description', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
          {canApply && (
            <Button variant="outline" disabled={applying} onClick={apply}>
              {applying ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} {t('brand.apply')}
            </Button>
          )}
          <Button disabled={saving} onClick={save}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} {t('brand.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
